import React from 'react';
import Dropzone from 'react-dropzone';
import { Controller } from 'react-hook-form';

import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import { inferMimeTypeFromName } from 'modules/file_manager/components/fileUtils/mimeFallback';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

function UploadInput({ control, errors }) {
  const { addToast } = useToast();
  const maxFiles = 20;
  // helper to ensure unique names
  const makeUniqueNames = (files, existingFiles = []) => {
    const nameCountMap = new Map();

    // count existing ones first
    existingFiles.forEach((file) => {
      nameCountMap.set(file.name, (nameCountMap.get(file.name) || 0) + 1);
    });

    return files.map((file) => {
      let name = file.name;
      const [base, ext] = (() => {
        const idx = name.lastIndexOf('.');
        return idx !== -1 ? [name.slice(0, idx), name.slice(idx)] : [name, ''];
      })();

      // if already exists, append (n)
      while (nameCountMap.has(name)) {
        const count = nameCountMap.get(name);
        name = `${base}(${count})${ext}`;
        nameCountMap.set(file.name, count + 1);
      }

      nameCountMap.set(name, 1);

      // return a *new* File object with updated name and fallback MIME type
      const fallbackType = file.type || inferMimeTypeFromName(name);
      return new File([file], name, { type: fallbackType });
    });
  };

  return (
    <div className="dropzone-wraper d-flex align-items-center py-2 flex-wrap">
      <div className="col-md-3 col-12">
        <label className="p-medium">
          Source <span style={{ color: 'red' }}>*</span>
        </label>
      </div>
      <div className="col-md-9 col-12">
        <Controller
          name="files"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value = [] } }) => (
            <Dropzone
              noDragEventsBubbling
              onDrop={(acceptedFiles, rejectedFiles) => {
                const allowedExtensions = [
                  'jpg',
                  'jpeg',
                  'png',
                  'heic',
                  'heif',
                  'pdf',
                  'doc',
                  'docx',
                  'xls',
                  'xlsx',
                  'txt',
                  'mov',
                  'wav',
                  'mp3',
                  'rtf',
                  'mpeg',
                  'mp4',
                  'aac',
                  '3gp',
                  'avi',
                  'wmv',
                  'wma',
                  'csv',
                ];
                const filteredFiles = acceptedFiles.filter((file) => {
                  const ext = file.name.split('.').pop().toLowerCase();
                  return allowedExtensions.includes(ext);
                });
                // ensure unique names
                const uniqueNewFiles = makeUniqueNames(filteredFiles, value);

                // append new files instead of replacing
                const updatedFiles = [...value, ...uniqueNewFiles];
                if (updatedFiles?.length <= maxFiles) {
                  onChange(updatedFiles);
                } else {
                  addToast(
                    false,
                    toastConstant.toasterType.WARN,
                    toastConstant.header.WARNINIG,
                    toastConstant.message.FILE_UPLOAD_LIMIT_EXCEEDED
                  );
                }

                if (rejectedFiles.length > 0) {
                  // You can show an error message or handle rejected files here
                }
              }}
              // Add accepted file types here
              accept=".jpg, .jpeg, .png, .heic, .heif, .pdf, .doc, .docx, .xls, .xlsx, .txt, .mov, .wav, .mp3, .rtf, .mpeg, .mp4, .aac, .3gp, .avi, .wmv, .wma, .csv"
            >
              {({ getRootProps, getInputProps }) => (
                <section>
                  <div {...getRootProps()}>
                    <input {...getInputProps()} directory="" />

                    <div className="text-center d-flex flex-column justify-content-center align-items-center file-manager-uploader mb-2">
                      <i className="icon-doc-add mb-2 pointer"></i>
                      <p className="F-size14">Click or drag file/folder to this area to upload</p>
                    </div>
                  </div>
                </section>
              )}
            </Dropzone>
          )}
        />
        {errors?.files && <InputErrorMessage>Required</InputErrorMessage>}
      </div>
    </div>
  );
}

export default UploadInput;
